import { useCallback, useEffect, useRef, useState } from 'react';
import { documentKind } from '@/library/documents';
import { extensionOf } from '@/library/prepare';
import { Panel, usePanelDocked } from './Panel';
import { Banner, EmptyState } from '@/ui/components/bits';
import { useAppState } from '@/ui/AppState';
import { FileViewer } from '@/ui/library/FileViewer';
import {
  addFile,
  deleteFile,
  listFiles,
  NotAvailableOffline,
  pinnedIds,
  RefusedFile,
  renameFile,
  searchFiles,
  setPinned,
  uploadPending,
} from '@/library/library';
import { formatBytes } from '@/db/persist';
import { ACCEPTED_EXTENSIONS, ACCEPTED_FILE_TYPES, LIBRARY_FILE_CAP_BYTES } from '@/constants/app';
import type { LibraryFile } from '@/domain/types';

type Busy = Record<string, string | undefined>;

export function LibraryPanel({ onClose }: { onClose: () => void }) {
  const { toast, reload, local } = useAppState();
  // Without a passcode there is no server to upload to, which is a setting,
  // not a failure. The difference changes what every message below says.
  const syncReady = Boolean(local?.token);
  const docked = usePanelDocked();
  const [files, setFiles] = useState<LibraryFile[]>([]);
  const [pinned, setPinnedSet] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>({});
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState<LibraryFile | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setFiles(await listFiles());
    setPinnedSet(await pinnedIds());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const mark = (id: string, label?: string) => setBusy((b) => ({ ...b, [id]: label }));

  /**
   * Adding files.
   *
   * A file is saved and pinned on this device the moment it is added, whether
   * or not there is a server to copy it to. Uploading is a separate step, and
   * when sync is not set up there is nothing to upload to — that is a setting
   * he has not reached yet, not a failure, and it must not read like one.
   */
  async function onPick(picked: File): Promise<boolean> {
    const { file, shrunkFrom } = await addFile(picked);
    await refresh();
    toast(
      shrunkFrom
        ? `Added ${file.name}, shrunk from ${formatBytes(shrunkFrom)} to ${formatBytes(file.size)}.`
        : `Added ${file.name}.`,
    );

    if (!syncReady) return true;

    mark(file.id, 'Uploading…');
    try {
      await uploadPending(file.id, (p) => mark(file.id, `Uploading ${p.done}/${p.total}…`));
    } catch (err) {
      toast(`${file.name} is saved here but did not upload: ${(err as Error).message}`);
    } finally {
      mark(file.id, undefined);
      await refresh();
      await reload();
    }
    return true;
  }

  /** Several at once: a term's worth of decks is picked in one go, not one by one. */
  async function onPickMany(picked: File[]) {
    setAdding(true);
    setError(null);
    const refused: string[] = [];
    try {
      for (const file of picked) {
        try {
          await onPick(file);
        } catch (err) {
          refused.push(
            err instanceof RefusedFile
              ? `${file.name}: ${err.message}`
              : `${file.name}: ${(err as Error).message}`,
          );
        }
      }
      // One banner for the whole pick, so adding nine files and having one
      // refused does not hide the eight that worked.
      if (refused.length > 0) setError(refused.join('\n'));
    } finally {
      setAdding(false);
      await refresh();
    }
  }


  async function retryUpload(file: LibraryFile) {
    mark(file.id, 'Uploading…');
    try {
      await uploadPending(file.id, (p) => mark(file.id, `Uploading ${p.done}/${p.total}…`));
      toast(`${file.name} uploaded.`);
    } catch (err) {
      toast((err as Error).message);
    } finally {
      mark(file.id, undefined);
      await refresh();
    }
  }

  async function togglePin(file: LibraryFile) {
    const isPinned = pinned.has(file.id);
    mark(file.id, isPinned ? 'Unpinning…' : 'Pinning…');
    try {
      await setPinned(file.id, !isPinned);
      toast(isPinned ? `${file.name} removed from this device.` : `${file.name} kept for offline.`);
    } catch (err) {
      toast(
        err instanceof NotAvailableOffline
          ? 'You need a connection to pin a file that is not already here.'
          : (err as Error).message,
      );
    } finally {
      mark(file.id, undefined);
      await refresh();
    }
  }

  const shown = searchFiles(files, query);

  return (
    <>
      <Panel title={viewing && docked ? viewing.name : 'Library'} onClose={onClose}>
        {viewing && docked ? (
          <FileViewer file={viewing} onClose={() => setViewing(null)} inline />
        ) : (
        <div className="stack">
          <p className="faint" style={{ margin: 0 }}>
            PDFs, images, slide decks and documents, up to {formatBytes(LIBRARY_FILE_CAP_BYTES)} each.
            Pinned files stay on
            this device and open with no connection.
          </p>

          <button
            className="btn btn--primary btn--block"
            disabled={adding}
            onClick={() => input.current?.click()}
          >
            {adding ? 'Adding…' : '+ Add a file'}
          </button>
          <input
            ref={input}
            type="file"
            accept={[...ACCEPTED_FILE_TYPES, ...ACCEPTED_EXTENSIONS].join(',')}
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const picked = [...(e.target.files ?? [])];
              if (picked.length > 0) void onPickMany(picked);
              e.target.value = '';
            }}
          />

          {error ? <Banner kind="error">{error}</Banner> : null}

          {files.length > 3 ? (
            <input
              className="input"
              type="search"
              placeholder="Search by filename"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          ) : null}

          {files.length === 0 ? (
            <EmptyState icon="📚" title="Nothing here yet">
              Add your lecture slides, recitation sheets, notes or photos of the board — PowerPoint
              and Word as they are, no exporting first. Decks open as their slides, text and
              pictures both.
            </EmptyState>
          ) : null}

          {shown.length === 0 && files.length > 0 ? (
            <p className="muted">No file matches that.</p>
          ) : null}

          <ul className="list">
            {shown.map((file) => {
              const isPinned = pinned.has(file.id);
              const label = busy[file.id];
              return (
                <li key={file.id} className="card stack" style={{ gap: 'var(--sp-2)' }}>
                  <div className="spread" style={{ gap: 'var(--sp-2)' }}>
                    <button
                      className="btn btn--ghost"
                      style={{ flex: 1, justifyContent: 'flex-start', padding: 0, minHeight: 0, textAlign: 'left' }}
                      onClick={() => setViewing(file)}
                    >
                      <span style={{ overflowWrap: 'anywhere', fontWeight: 500 }}>{file.name}</span>
                    </button>
                    <button
                      className="icon-btn"
                      aria-label={isPinned ? `Unpin ${file.name}` : `Pin ${file.name}`}
                      aria-pressed={isPinned}
                      title={isPinned ? 'Kept on this device' : 'Not kept on this device'}
                      disabled={Boolean(label)}
                      onClick={() => void togglePin(file)}
                    >
                      {isPinned ? '★' : '☆'}
                    </button>
                  </div>

                  <div className="row faint" style={{ gap: 'var(--sp-2)' }}>
                    <span className="mono">{formatBytes(file.size)}</span>
                    <span>·</span>
                    <span>{kindLabel(file.mime, file.name)}</span>
                    {file.pageCount ? (
                      <>
                        <span>·</span>
                        <span>{file.pageCount} pages</span>
                      </>
                    ) : null}
                    <span>·</span>
                    {label ? (
                      <span style={{ color: 'var(--accent)' }}>{label}</span>
                    ) : file.uploaded ? (
                      <span style={{ color: 'var(--correct)' }}>backed up</span>
                    ) : syncReady ? (
                      <span style={{ color: 'var(--incorrect)' }}>not uploaded</span>
                    ) : (
                      <span>on this device</span>
                    )}
                  </div>

                  {renaming === file.id ? (
                    <div className="row" style={{ gap: 'var(--sp-2)' }}>
                      <input
                        className="input"
                        style={{ flex: 1 }}
                        value={draftName}
                        autoFocus
                        onChange={(e) => setDraftName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          if (e.key === 'Escape') setRenaming(null);
                        }}
                      />
                      <button
                        className="btn btn--sm btn--primary"
                        onClick={async () => {
                          await renameFile(file.id, draftName);
                          setRenaming(null);
                          await refresh();
                          await reload();
                        }}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <div className="row" style={{ gap: 'var(--sp-2)' }}>
                      <button className="btn btn--sm" onClick={() => setViewing(file)}>
                        Open
                      </button>
                      <button
                        className="btn btn--sm"
                        onClick={() => {
                          setRenaming(file.id);
                          setDraftName(file.name);
                        }}
                      >
                        Rename
                      </button>
                      {!file.uploaded && syncReady ? (
                        <button
                          className="btn btn--sm"
                          disabled={Boolean(label)}
                          onClick={() => void retryUpload(file)}
                        >
                          Retry upload
                        </button>
                      ) : null}
                      <button
                        className="btn btn--sm btn--danger-text"
                        onClick={() => setConfirmDelete(file.id)}
                      >
                        Delete
                      </button>
                    </div>
                  )}

                  {confirmDelete === file.id ? (
                    <Banner kind="warn">
                      <strong>Delete {file.name}?</strong>
                      <div>It is removed from this device and from your other one.</div>
                      <div className="row" style={{ marginTop: 'var(--sp-2)' }}>
                        <button
                          className="btn btn--sm btn--bad"
                          onClick={async () => {
                            setConfirmDelete(null);
                            mark(file.id, 'Deleting…');
                            await deleteFile(file.id);
                            mark(file.id, undefined);
                            await refresh();
                            await reload();
                            toast(`Deleted ${file.name}.`);
                          }}
                        >
                          Delete
                        </button>
                        <button className="btn btn--sm" onClick={() => setConfirmDelete(null)}>
                          Cancel
                        </button>
                      </div>
                    </Banner>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
        )}
      </Panel>

      {/* Expanded, it still takes the whole screen, which is what expanding is for. */}
      {viewing && !docked ? (
        <FileViewer file={viewing} onClose={() => setViewing(null)} />
      ) : null}
    </>
  );
}

/** What to call a file in the list, in one word. */
function kindLabel(mime: string, name: string): string {
  switch (documentKind(mime, name)) {
    case 'pdf':
      return 'PDF';
    case 'image':
      return 'Image';
    case 'pptx':
    case 'odp':
      return 'Slides';
    case 'docx':
    case 'odt':
      return 'Document';
    case 'text':
      return 'Text';
    default:
      return extensionOf(name).toUpperCase() || 'File';
  }
}
